import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { AsYouType } from 'libphonenumber-js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { cn } from '@/lib/utils';

type CountryCode = 'US' | 'CA';

// NANP only — US and Canada both use +1 and the same national format, so the selector
// is presentational (it never changes the stored E.164).
const COUNTRIES: { code: CountryCode; label: string; flag: string }[] = [
  { code: 'US', label: 'United States', flag: '🇺🇸' },
  { code: 'CA', label: 'Canada', flag: '🇨🇦' },
];

type PhoneInputProps = Omit<ComponentProps<'input'>, 'onChange' | 'value'> & {
  value: string;
  onChange: (value: string) => void;
  /**
   * Leave input that is not a US/CA number as typed (and emit it as `+digits`)
   * instead of trimming it to ten digits, so the caller's validation can flag
   * it. Without this a pasted "+44 20 7946 0958" silently becomes the
   * different, valid-looking "+14420794609".
   */
  strict?: boolean;
};

const onlyDigits = (value: string) => value.replace(/\D/g, '');

/**
 * True when `raw` cannot be a NANP number: another country code anywhere, or —
 * for pasted text only — too many digits. Typing past ten digits is simply
 * ignored, as without `strict`; only a paste is kept to be flagged.
 */
function isForeignNumber(raw: string, inputType: string | undefined): boolean {
  const trimmed = raw.trim();
  if (trimmed.startsWith('+') && !trimmed.startsWith('+1')) return onlyDigits(trimmed).length > 0;
  if (inputType !== 'insertFromPaste' && inputType !== 'insertFromDrop') return false;
  const digits = onlyDigits(trimmed);
  return digits.length > 11 || (digits.length === 11 && !digits.startsWith('1'));
}

/** National NANP digits (max 10); strips a leading country-code "1" when pasted. */
function toNationalDigits(raw: string): string {
  let digits = onlyDigits(raw);
  if (digits.length > 10 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.slice(0, 10);
}

function formatNational(digits: string, country: CountryCode): string {
  return digits ? new AsYouType(country).input(digits) : '';
}

/** Caret offset just after the `n`-th digit in a formatted string. */
function caretAfterDigits(formatted: string, n: number): number {
  if (n <= 0) return 0;
  let count = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i]!)) {
      count += 1;
      if (count === n) return i + 1;
    }
  }
  return formatted.length;
}

function NanpCountrySelect({
  value,
  onChange,
}: {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = COUNTRIES.find((entry) => entry.code === value) ?? COUNTRIES[0]!;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-full shrink-0 items-center gap-1 rounded-l-md border-r border-input px-2.5 text-sm hover:bg-muted/50 focus-visible:outline-none"
          aria-label={`Country: ${active.label}`}
        >
          <span className="text-base leading-none">{active.flag}</span>
          <span className="text-muted-foreground">+1</span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        {COUNTRIES.map((entry) => (
          <button
            key={entry.code}
            type="button"
            onClick={() => {
              onChange(entry.code);
              setOpen(false);
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted',
              entry.code === value && 'bg-muted',
            )}
          >
            <span className="text-base leading-none">{entry.flag}</span>
            <span className="flex-1 text-left">{entry.label}</span>
            <span className="text-muted-foreground">+1</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Controlled phone input for the NANP (US/CA, both +1). `value` in/out is E.164
 * ("+15551234567"); the field renders national "(555) 123-4567" as you type, preserving
 * the caret and supporting natural backspace (deletes through formatting separators).
 *
 * Built on libphonenumber-js's `AsYouType`, not react-phone-number-input — the latter's
 * bidirectional value-sync effects loop into "Maximum update depth exceeded" on React 19.
 *
 * The US/CA selector is presentational (both share +1 and the same national format, so it
 * never changes the stored E.164). It is session-local, defaulting to US — the contact
 * model has no phone-country field to persist it to.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, className, strict = false, ...rest },
  ref,
) {
  const { 'aria-invalid': ariaInvalid, ...inputRest } = rest;
  const invalid = ariaInvalid === true || ariaInvalid === 'true';

  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastEmittedRef = useRef(value);
  const caretDigitsRef = useRef<number | null>(null);
  const [country, setCountry] = useState<CountryCode>('US');
  const [display, setDisplay] = useState(() => formatNational(toNationalDigits(value), country));

  // Re-sync from an external value change (form reset / prefill); ignore our own echo.
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      setDisplay(formatNational(toNationalDigits(value), country));
    }
  }, [value, country]);

  // Restore the caret to the same digit offset after a formatting re-render.
  useLayoutEffect(() => {
    if (caretDigitsRef.current === null) return;
    const node = inputRef.current;
    if (node) {
      const pos = caretAfterDigits(display, caretDigitsRef.current);
      node.setSelectionRange(pos, pos);
    }
    caretDigitsRef.current = null;
  }, [display]);

  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value;
    if (strict && isForeignNumber(raw, (event.nativeEvent as InputEvent).inputType)) {
      setDisplay(raw);
      const asTyped = `+${onlyDigits(raw)}`;
      lastEmittedRef.current = asTyped;
      onChange(asTyped);
      return;
    }
    const caret = event.target.selectionStart ?? raw.length;
    const inputType = (event.nativeEvent as InputEvent).inputType;

    let digitsBeforeCaret = onlyDigits(raw.slice(0, caret)).length;
    let digits = toNationalDigits(raw);

    // A single backspace that removed a formatting separator: also remove the digit
    // before it, so deleting feels natural across "(", ")", "-" and spaces.
    if (inputType === 'deleteContentBackward' && raw.length === display.length - 1) {
      const deleted = display.charAt(caret);
      if (deleted && !/\d/.test(deleted) && digitsBeforeCaret > 0) {
        digits = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
        digitsBeforeCaret -= 1;
      }
    }

    caretDigitsRef.current = digitsBeforeCaret;
    setDisplay(formatNational(digits, country));

    const e164 = digits ? `+1${digits}` : '';
    lastEmittedRef.current = e164;
    onChange(e164);
  }

  return (
    <div
      data-invalid={invalid ? 'true' : undefined}
      className={cn(
        'flex h-9 items-center rounded-md border border-input bg-transparent shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 data-[invalid=true]:border-destructive data-[invalid=true]:ring-[3px] data-[invalid=true]:ring-destructive/20 dark:bg-input/30',
        className,
      )}
    >
      <NanpCountrySelect value={country} onChange={setCountry} />
      <input
        ref={setRefs}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        aria-invalid={ariaInvalid}
        value={display}
        onChange={handleChange}
        className="h-full w-full min-w-0 bg-transparent px-3 py-1 text-base text-foreground outline-none placeholder:text-muted-foreground md:text-sm"
        {...inputRest}
      />
    </div>
  );
});
